import {
  Badge,
  Box,
  EmptyState,
  Group,
  RingProgress,
  Stack,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";

import type { CoalOut, LabAttributeOut } from "../../api/generated/model";
import { MiniBar } from "../../components/bits";
import { IconMountain } from "../../components/icons";
import { coverageText, FALLBACK_ATTRS, fmtLab, labLine } from "../../lib/lab";
import {
  FLAG_LABEL,
  FLAGS,
  fmt0,
  fmtPct,
  fmtRange,
  fmtSigned,
  type PileRow,
  totals,
} from "./tables";

const FLAG_COLOUR: Record<string, string> = {
  E: "yellow",
  C: "gray",
  B: "orange",
  D: "violet",
  N: "teal",
};

/** The lab values of a pile's coal (M7.9): the yard's in the totals row. */
export interface CoalQuality {
  piles: Map<string, CoalOut>;
  yard: CoalOut | null;
  attrs: LabAttributeOut[];
}

/** One row per pile of the latest run, along the arc, with the totals that add up to them; an
 * "Ash %" column when the lab has results for some of the coal. */
export function PileTable({
  rows,
  colourOf,
  hovered = null,
  onHover,
  onPile,
  quality = null,
}: {
  rows: PileRow[];
  colourOf: (product: string | null | undefined) => string;
  hovered?: string | null;
  onHover?: (code: string | null) => void;
  onPile?: (code: string) => void;
  quality?: CoalQuality | null;
}) {
  const t = totals(rows);
  const anyFlags = rows.some((r) => r.flags.length > 0);
  const showAsh = quality !== null && [...quality.piles.values()].some((c) => c.coverage > 0);
  const attrs = quality?.attrs ?? FALLBACK_ATTRS;
  const ash = attrs.find((a) => a.key === "ash_pct") ?? FALLBACK_ATTRS[0]!;
  if (rows.length === 0)
    return (
      <EmptyState
        icon={<IconMountain size={28} />}
        title="No piles in the last sweep"
        description="The pad is empty, or the sweep did not cover it."
        py="xl"
      />
    );
  return (
    <Table
      highlightOnHover
      fz="sm"
      data-testid="pile-table"
      onMouseLeave={() => onHover?.(null)}
      style={{ whiteSpace: "nowrap" }}
    >
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Pile</Table.Th>
          <Table.Th ta="right">Volume m³</Table.Th>
          <Table.Th ta="right">Tonnes</Table.Th>
          <Table.Th ta="right">Change m³</Table.Th>
          <Table.Th ta="center">Seen</Table.Th>
          {showAsh && <Table.Th ta="right">Ash %</Table.Th>}
          {anyFlags && <Table.Th>Flags</Table.Th>}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((r) => (
          <Table.Tr
            key={r.code}
            onClick={() => onPile?.(r.code)}
            onMouseEnter={() => onHover?.(r.code)}
            className={hovered === r.code ? "sv-row-active" : undefined}
            style={{ cursor: onPile ? "pointer" : undefined }}
            data-testid={`row-${r.code}`}
          >
            <Table.Td>
              <Group gap={10} wrap="nowrap">
                <Box
                  aria-hidden="true"
                  w={10}
                  h={10}
                  style={{
                    borderRadius: 3,
                    background: colourOf(r.product),
                    flex: "none",
                  }}
                />
                <Stack gap={0}>
                  <Group gap={6} wrap="nowrap" align="baseline">
                    <Text size="sm" fw={600} lh={1.3}>
                      {r.label ?? r.code}
                    </Text>
                    {r.label && (
                      <Text size="xs" c="dimmed" ff="monospace">
                        {r.code}
                      </Text>
                    )}
                  </Group>
                  <Text size="xs" c="dimmed" lh={1.3}>
                    {r.product ?? "no product"} · {r.positionFromM.toFixed(0)}–
                    {r.positionToM.toFixed(0)} m
                  </Text>
                </Stack>
              </Group>
            </Table.Td>
            <Table.Td ta="right">
              <Stack gap={3} align="flex-end">
                <Tooltip label={`range ${fmtRange(r.vMin, r.vMax)} m³`}>
                  <Text size="sm" fw={600}>
                    {fmt0(r.vBest)}
                  </Text>
                </Tooltip>
                <MiniBar
                  frac={t.vBest > 0 ? (r.vBest ?? 0) / t.vBest : 0}
                  colour={colourOf(r.product)}
                />
              </Stack>
            </Table.Td>
            <Table.Td ta="right">
              {fmt0(r.tonnes)}
              {r.tonnesSigma ? (
                <Text span c="dimmed" size="xs">
                  {" "}
                  ±{fmt0(r.tonnesSigma)}
                </Text>
              ) : null}
            </Table.Td>
            <Table.Td ta="right">
              <Change stacked={r.stacked} reclaimed={r.reclaimed} />
            </Table.Td>
            <Table.Td>
              <Tooltip
                label={`${fmtPct(r.observedFrac)} of the pile seen by the last sweep`}
              >
                <Group gap={4} justify="center" wrap="nowrap">
                  <RingProgress
                    size={22}
                    thickness={3}
                    roundCaps
                    sections={[
                      {
                        value: Math.round(r.observedFrac * 100),
                        color: r.observedFrac >= 0.9 ? "teal" : "yellow",
                      },
                    ]}
                  />
                  <Text size="xs" c="dimmed">
                    {fmtPct(r.observedFrac)}
                  </Text>
                </Group>
              </Tooltip>
            </Table.Td>
            {showAsh && (
              <Table.Td ta="right" className="sv-num">
                <AshCell coal={quality?.piles.get(r.code)} ash={ash} attrs={attrs} />
              </Table.Td>
            )}
            {anyFlags && (
              <Table.Td>
                <Group gap={4} wrap="nowrap">
                  {[...r.flags].map((f) => (
                    <Tooltip key={f} label={FLAGS[f]}>
                      <Badge
                        size="sm"
                        variant="light"
                        color={FLAG_COLOUR[f] ?? "gray"}
                        tt="none"
                      >
                        {FLAG_LABEL[f] ?? f}
                      </Badge>
                    </Tooltip>
                  ))}
                </Group>
              </Table.Td>
            )}
          </Table.Tr>
        ))}
      </Table.Tbody>
      <Table.Tfoot>
        <Table.Tr fw={700} data-testid="pile-totals">
          <Table.Td>Total</Table.Td>
          <Table.Td ta="right">{fmt0(t.vBest)}</Table.Td>
          <Table.Td ta="right">{fmt0(t.tonnes)}</Table.Td>
          <Table.Td ta="right">
            <Change stacked={t.stacked} reclaimed={t.reclaimed} bold />
          </Table.Td>
          <Table.Td />
          {showAsh && (
            <Table.Td ta="right" className="sv-num">
              {fmtLab(quality?.yard?.values.ash_pct, ash)}
            </Table.Td>
          )}
          {anyFlags && <Table.Td />}
        </Table.Tr>
      </Table.Tfoot>
    </Table>
  );
}

/** A pile's ash, with the other lab values and the coverage on hover. */
function AshCell({ coal, ash, attrs }: { coal: CoalOut | undefined; ash: LabAttributeOut; attrs: LabAttributeOut[] }) {
  if (!coal || coal.coverage <= 0)
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  return (
    <Tooltip label={`${labLine(coal.values, attrs)} · ${coverageText(coal.coverage)}`}>
      <Text size="sm" span>
        {fmtLab(coal.values.ash_pct, ash)}
      </Text>
    </Tooltip>
  );
}

/** Stacked over reclaimed (each its own element), coloured when it is more than nothing. */
function Change({
  stacked,
  reclaimed,
  bold = false,
}: {
  stacked: number;
  reclaimed: number;
  bold?: boolean;
}) {
  const up = Math.round(stacked) > 0;
  const down = Math.round(reclaimed) < 0;
  if (!up && !down)
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  return (
    <Stack gap={0} align="flex-end">
      <Text
        size="sm"
        fw={bold ? 700 : 500}
        c={up ? "teal.8" : "dimmed"}
        lh={1.3}
      >
        {fmtSigned(stacked)}
      </Text>
      <Text
        size="sm"
        fw={bold ? 700 : 500}
        c={down ? "blue.8" : "dimmed"}
        lh={1.3}
      >
        {fmtSigned(reclaimed)}
      </Text>
    </Stack>
  );
}
